import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { GARDEN_NAMES } from '../../../lib/garden';
import { SCALE4, SCALE5, INTEREST, labelOf } from '../../../lib/reviewLabels';
import AdminAuthGuard from '../../../components/feature/AdminAuthGuard';

/**
 * 帰り道のアンケート（worker_reviews）の管理画面。
 *
 * 🟥 timeeの/adminは現状、園ごとの権限分けが無い一枚の管理者ログイン
 *    （2026-09-27確認：/admin/register にgarden_slugの指定が無い）。
 *    それに合わせて、ここも「ログインしていれば全園ぶん読める・園で絞り込める」形にする。
 *    園ごとに見せる範囲を分けるのは、/adminの認証モデルそのものを作り直すときにやる。
 */

const GOLD = '#a8935f';

type ReviewRow = {
  id: string;
  garden_slug: string;
  q_arrival: number | null;
  q_whom_to_ask: number | null;
  q_where_things: number | null;
  good_moment: string | null;
  idea: string | null;
  concern: string | null;
  want_again: number | null;
  interest: string | null;
  contact_name: string | null;
  contact: string | null;
  created_at: string;
};

function fmtDateTime(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function ScorePill({ label, value, options }: { label: string; value: number | null; options: { v: number; label: string }[] }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[14px]">
      <span className="text-gray-500">{label}</span>
      <span className="font-bold text-gray-800">{labelOf(options, value)}</span>
    </div>
  );
}

export default function AdminReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [gardenFilter, setGardenFilter] = useState('');

  useEffect(() => {
    supabase
      .from('worker_reviews')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error: e }) => {
        if (e) { setError(true); return; }
        setReviews((data ?? []) as ReviewRow[]);
      })
      .finally(() => setLoading(false));
  }, []);

  const gardenOptions = useMemo(() => {
    const slugs = Array.from(new Set(reviews.map((r) => r.garden_slug)));
    return slugs.sort();
  }, [reviews]);

  const filtered = gardenFilter ? reviews.filter((r) => r.garden_slug === gardenFilter) : reviews;

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg" style={{ background: '#faf7f0' }}>
                <i className="ri-chat-smile-2-line text-xl sm:text-2xl" style={{ color: GOLD }}></i>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">帰り道のアンケート</h1>
                <p className="text-sm text-gray-500 hidden sm:block">ワーカーさんからいただいた声（{filtered.length}件）</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/admin/workers" className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-teal-600 transition-colors">
                <i className="ri-team-line text-lg"></i>
                <span className="text-sm hidden sm:inline">ワーカー管理</span>
              </Link>
              <button
                onClick={async () => { await supabase.auth.signOut(); navigate('/admin/login'); }}
                className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-red-600 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-line text-lg"></i>
                <span className="text-sm hidden sm:inline">ログアウト</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
          {gardenOptions.length > 1 && (
            <div className="mb-4">
              <select
                value={gardenFilter}
                onChange={(e) => setGardenFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">すべての園</option>
                {gardenOptions.map((slug) => (
                  <option key={slug} value={slug}>{GARDEN_NAMES[slug] ?? slug}</option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div className="text-center text-gray-400 py-16 text-sm">読み込み中…</div>
          ) : error ? (
            <div className="text-center text-red-500 py-16 text-sm">取得できませんでした</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-400 py-16 text-sm">まだ回答がありません</div>
          ) : (
            <div className="space-y-4">
              {filtered.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[13px] font-bold" style={{ color: GOLD }}>
                      {GARDEN_NAMES[r.garden_slug] ?? r.garden_slug}
                    </span>
                    <span className="text-[12px] text-gray-400">{fmtDateTime(r.created_at)}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 border-t border-gray-100 pt-2 mb-3">
                    <ScorePill label="着いてから分かったか" value={r.q_arrival} options={SCALE4} />
                    <ScorePill label="誰に聞けばいいか分かったか" value={r.q_whom_to_ask} options={SCALE4} />
                    <ScorePill label="物の場所が分かったか" value={r.q_where_things} options={SCALE4} />
                    <ScorePill label="また来たいか" value={r.want_again} options={SCALE5} />
                  </div>

                  {(r.good_moment || r.idea || r.concern) && (
                    <div className="space-y-2.5 border-t border-gray-100 pt-3 mb-3">
                      {r.good_moment && (
                        <div>
                          <p className="text-[12px] font-bold text-gray-400 mb-0.5">うまくいった場面</p>
                          <p className="text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">{r.good_moment}</p>
                        </div>
                      )}
                      {r.idea && (
                        <div>
                          <p className="text-[12px] font-bold text-gray-400 mb-0.5">他園の工夫・アイデア</p>
                          <p className="text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">{r.idea}</p>
                        </div>
                      )}
                      {r.concern && (
                        <div>
                          <p className="text-[12px] font-bold text-gray-400 mb-0.5">気になったところ</p>
                          <p className="text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">{r.concern}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 text-[13px]">
                    {r.interest && (
                      <span className="px-2.5 py-1 rounded-full font-bold" style={{ background: '#faf7f0', color: GOLD }}>
                        {labelOf(INTEREST, r.interest)}
                      </span>
                    )}
                    {(r.contact_name || r.contact) && (
                      <span className="text-gray-600">
                        {r.contact_name}{r.contact_name && r.contact ? '・' : ''}{r.contact}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </AdminAuthGuard>
  );
}
